import { API_BASE_URL } from "../config";
import axios from "axios";

export interface AvailableDatasetsResponse {
  data: string[];
}

export interface PathwayDataset {
  id: string;
  name: string;
  description?: string;
  uploaded_at?: string;
  file_size?: number;
  row_count?: number;
}

export interface UploadDatasetResponse {
  success: boolean;
  dataset_id: string;
  message?: string;
  row_count?: number;
}

export interface PathwayType {
  id: string;
  name: string;
  description?: string;
  count?: number;
}

export interface Pathway {
  id: string;
  name: string;
  description?: string;
  pathway_type?: string;
  gene_count?: number;
  source?: string;
}

export interface PathwayColumn {
  name: string;
  type: string;
  description?: string;
  nullable?: boolean;
}

export interface PathwayGene {
  gene_id: string;
  gene_symbol: string;
  gene_name?: string;
  pathway_id: string;
  pathway_name: string;
  role?: string;
  evidence?: string;
}

export interface OneHopNeighborEdge  {
  source: string;
  target: string;
  path: string[];
  path_length: number;
}

export interface OneHopNeighborsResponse {
  data: OneHopNeighborEdge[];
}

export interface NeighborNode {
  gene_id: string;
  gene_symbol: string;
  gene_name?: string;
  distance?: number;
  relationship_type?: string;
  interaction_score?: number;
}

export interface KHopNeighborsResponse {
  source_gene: string;
  k: number;
  neighbors: NeighborNode[];
  total_neighbors: number;
  paths_count?: number;
}

export interface HighlightedGene {
  gene_id: string;
  gene_symbol: string;
  gene_name?: string;
  highlight_reason?: string;
  score?: number;
  pathway_associations?: string[];
}

export interface GetAvailablePathwaysParams {
  pathway_type?: string;
  dataset?: string;
  limit?: number;
  offset?: number;
}

export interface GetPathwayGenesParams {
  pathway: string;
  dataset: string;
  limit?: number;
  offset?: number;
}

export interface GetOneHopNeighborsParams {
  edgetype: string;
  pathway_genes: string[];
  edge_threshold?: number;
}

export interface GetKHopNeighborsParams {
  edgetype: string;
  selected_genes: string[];
  k?: number;
  edge_threshold?: number;
}

/**
 * Whether each relation type is directed in the graph API.
 * Matches backend RELATION_TYPE_DIRECTED.
 */
export const RELATION_TYPE_DIRECTED: Readonly<Record<string, boolean>> = {
  INTERACTS_WITH: false,
  ASSOCIATED: false,
  CO_EXPRESSED: false,
  CO_OCCURS: false,
  UP_REGULATES: true,
  DOWN_REGULATES: true,
  PHYSICALLY_INTERACTS: false,
  REGULATES_TRANSCRIPTION: true,
  REPRESSES_TRANSCRIPTION: true,
  ACTIVATES_TRANSCRIPTION: true,
  ACTS_ON: true,
} as const;

export function isRelationDirected(edgetype: string): boolean {
  return RELATION_TYPE_DIRECTED[edgetype] ?? false;
}

/**
 * Edge filter params for neighbor APIs:
 * - threshold <= 0: no edge_attribute (and no edge_threshold in query)
 * - INTERACTS_WITH + threshold > 0: avg_score
 * - other types + threshold > 0: score
 */
export function buildPathwayNeighborQueryOptions(
  edgetype: string,
  edgeThreshold: number
): { directed: boolean; edge_attribute?: string; edge_threshold?: number } {
  const directed = isRelationDirected(edgetype);
  if (edgeThreshold > 0) {
    return {
      directed,
      edge_attribute: edgetype === "INTERACTS_WITH" ? "avg_score" : "score",
      edge_threshold: edgeThreshold,
    };
  }
  return { directed };
}

export interface GetHighlightedGenesParams {
  dataset: string;
  pathway: string;
  highlight_col?: string | null;
}

class PathwayExplorerClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get all available pathway datasets
   */
  async getAvailableDatasets(): Promise<string[]> {
    try {
      const response = await axios.get<AvailableDatasetsResponse>(
        `${this.baseURL}/api/gene-hub/pathway-explorer/available-datasets`
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching available datasets:', error);
      throw error;
    }
  }

  /**
   * Upload a new pathway dataset
   * @param file - The file to upload
   * @param name - Optional name for the dataset
   * @param description - Optional description for the dataset
   */
  async uploadDataset(
    file: File,
    name?: string,
    description?: string
  ): Promise<UploadDatasetResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (name) {
        formData.append('name', name);
      }
      
      if (description) {
        formData.append('description', description);
      }

      const response = await axios.post(
        `${this.baseURL}/api/gene-hub/pathway-explorer/upload-dataset`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading dataset:', error);
      throw error;
    }
  }

  /**
   * Get all available pathway types
   */
  async getAvailablePathwayTypes(dataset: string): Promise<string[] | null> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/available-pathway-types`,
        { params: { dataset } }
      );
      return response.data?.data ?? null;
    } catch (error) {
      console.error('Error fetching pathway types:', error);
      throw error;
    }
  }

  /**
   * Get pathways with optional filtering
   * @param params - Optional parameters for filtering pathways
   */
  async getAvailablePathways(params?: GetAvailablePathwaysParams): Promise<string[]> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/available-pathways`,
        { params }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching available pathways:', error);
      throw error;
    }
  }

  /**
   * Get data columns for pathway data
   * @param datasetId - Optional dataset ID to get columns for
   */
  async getPathwayDataColumns(dataset: string): Promise<string[]> {
    try {
      const params = { dataset: dataset };
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/pathway-data-columns`,
        { params }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching pathway data columns:', error);
      throw error;
    }
  }

  /**
   * Get genes for a specific pathway
   * @param params - Parameters including pathway_id and optional filters
   */
  async getPathwayGenes(params: GetPathwayGenesParams): Promise<PathwayGene[]> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/pathway-genes`,
        { params }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching pathway genes:', error);
      throw error;
    }
  }

  /**
   * Get one-hop neighbors for a gene
   * @param params - Parameters including gene_id and optional filters
   */
  async getOneHopNeighbors(params: GetOneHopNeighborsParams): Promise<OneHopNeighborEdge[]> {
    try {
      const { edgetype, pathway_genes, edge_threshold = 0 } = params;
      const queryParams = {
        edgetype,
        pathway_genes,
        ...buildPathwayNeighborQueryOptions(edgetype, edge_threshold),
      };
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/one-hop-neighbors`,
        {
          params: queryParams,
          paramsSerializer: (params) => {
            const parts: string[] = [];
            for (const [key, value] of Object.entries(params)) {
              if (Array.isArray(value)) {
                value.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`));
              } else if (value !== null && value !== undefined) {
                parts.push(`${key}=${encodeURIComponent(value)}`);
              }
            }
            return parts.join("&");
          },
        }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching one-hop neighbors:', error);
      throw error;
    }
  }

  /**
   * Get k-hop neighbors for a gene
   * @param params - Parameters including gene_id, k value, and optional filters
   */
  async getKHopNeighbors(params: GetKHopNeighborsParams): Promise<OneHopNeighborEdge[]> {
    try {
      const { edgetype, selected_genes, k, edge_threshold = 0 } = params;
      const queryParams = {
        edgetype,
        selected_genes,
        ...(k !== undefined && k !== null ? { k } : {}),
        ...buildPathwayNeighborQueryOptions(edgetype, edge_threshold),
      };
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/k-hop-neighbors`,
        { params: queryParams,
          paramsSerializer: (params) => {
            const parts: string[] = [];
            for (const [key, value] of Object.entries(params)) {
              if (Array.isArray(value)) {
                value.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`));
              } else if (value !== null && value !== undefined) {
                parts.push(`${key}=${encodeURIComponent(value)}`);
              }
            }
            return parts.join("&");
          },
         }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching k-hop neighbors:', error);
      throw error;
    }
  }

  /**
   * Get highlighted genes based on criteria
   * @param params - Optional parameters for filtering highlighted genes
   */
  async getHighlightedGenes(params?: GetHighlightedGenesParams): Promise<string[]> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/gene-hub/pathway-explorer/highlighted-genes`,
        { params }
      );
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching highlighted genes:', error);
      throw error;
    }
  }
}

// Create and export a default instance
export const pathwayExplorerClient = new PathwayExplorerClient();

// Export the class for custom instances
export default PathwayExplorerClient;
